import { formatCurrency } from './formatCurrency';
import { IntlShape } from 'react-intl';

export interface CancellationPolicyData {
  allowCancelDuring?: boolean;
  allowLateCancel?: boolean;
  cancelDuringFeeAmount?: number;
  cancelDuringFeePercent?: number;
  lateCancelFeeAmount?: number;
  lateCancelFeePercent?: number;
  cancellationWindowEndMinutes?: number;
  cancellationDescription?: string;
  description?: string;
  currencyType?: string;
  locale?: string;
}

const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;
const MINUTES_IN_WEEK = 7 * MINUTES_IN_DAY;

const hasFee = (amount?: number, percent?: number): boolean => {
  return (amount ?? 0) > 0 || (percent ?? 0) > 0;
};

const formatDuration = (minutes?: number | null, intl?: IntlShape): string | null => {
  if (!minutes || minutes <= 0) return null;
  if (minutes % MINUTES_IN_WEEK === 0) {
    const weeks = minutes / MINUTES_IN_WEEK;
    return intl
      ? intl.formatMessage({ id: 'cancellation_duration_weeks' }, { count: weeks })
      : `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  }
  if (minutes % MINUTES_IN_DAY === 0) {
    const days = minutes / MINUTES_IN_DAY;
    return intl
      ? intl.formatMessage({ id: 'cancellation_duration_days' }, { count: days })
      : `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (minutes % MINUTES_IN_HOUR === 0) {
    const hours = minutes / MINUTES_IN_HOUR;
    return intl
      ? intl.formatMessage({ id: 'cancellation_duration_hours' }, { count: hours })
      : `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  return intl ? intl.formatMessage({ id: 'cancellation_duration_minutes' }, { count: minutes }) : `${minutes} minutes`;
};

const formatFee = (amount?: number, percent?: number, currencyType?: string, locale?: string): string | null => {
  if ((percent ?? 0) > 0) {
    return `${percent}%`;
  }
  if ((amount ?? 0) > 0) {
    if (currencyType && locale) {
      return formatCurrency(amount ?? 0, currencyType, locale);
    }
    const decimalAmount = (amount ?? 0) / 100;
    return decimalAmount.toFixed(2);
  }
  return null;
};

const normalizeCustomMessage = (message?: string): string | null => {
  const trimmedMessage = message?.trim();
  if (!trimmedMessage) return null;
  return trimmedMessage.replace(/\n/g, '<br/>');
};

export const getCancellationPolicyText = (
  data: CancellationPolicyData,
  intl?: IntlShape,
  isPaidResource = true,
): string | null => {
  const {
    allowCancelDuring = false,
    allowLateCancel = false,
    cancelDuringFeeAmount,
    cancelDuringFeePercent,
    lateCancelFeeAmount,
    lateCancelFeePercent,
    cancellationWindowEndMinutes,
    cancellationDescription,
    description,
    currencyType,
    locale,
  } = data;
  if (!(allowCancelDuring || allowLateCancel) && !cancellationWindowEndMinutes) {
    return null;
  }

  const windowText = formatDuration(cancellationWindowEndMinutes, intl);
  const customMessage = normalizeCustomMessage(cancellationDescription ?? description);
  const hasPaidFees =
    isPaidResource &&
    ((allowCancelDuring && hasFee(cancelDuringFeeAmount, cancelDuringFeePercent)) ||
      (allowLateCancel && hasFee(lateCancelFeeAmount, lateCancelFeePercent)));
  const rules: string[] = [];
  if (!allowLateCancel) {
    if (windowText) {
      rules.push(
        intl
          ? intl.formatMessage({ id: 'cancellation_must_be_made_before' }, { duration: windowText })
          : `• Cancellations must be made at least ${windowText} before start`,
      );
      const rulesText = rules.filter(Boolean).join('<br/>');

      if (rulesText && customMessage) {
        return `${rulesText}<br/><br/>${customMessage}`;
      }

      return rulesText || customMessage;
    }
  } else if (windowText) {
    if (hasPaidFees) {
      rules.push(
        intl
          ? intl.formatMessage({ id: 'cancellation_free_up_to' }, { duration: windowText })
          : `• Free cancellation up to ${windowText} before start`,
      );

      const feeText = formatFee(lateCancelFeeAmount, lateCancelFeePercent, currencyType, locale);
      rules.push(
        intl
          ? intl.formatMessage({ id: 'cancellation_fee_within' }, { fee: feeText, duration: windowText })
          : `• ${feeText} fee for cancellations within ${windowText}`,
      );
    } else {
      rules.push(
        intl
          ? intl.formatMessage({ id: 'cancellation_marked_as_late' }, { duration: windowText })
          : `• Cancellations within ${windowText} will be marked as late`,
      );
    }
  }

  if (allowCancelDuring) {
    const cancelDuringFeeText = formatFee(cancelDuringFeeAmount, cancelDuringFeePercent, currencyType, locale);
    if (cancelDuringFeeText && hasPaidFees) {
      rules.push(
        intl
          ? intl.formatMessage({ id: 'cancellation_fee_after_start' }, { fee: cancelDuringFeeText })
          : `• ${cancelDuringFeeText} fee for cancellations after booking starts`,
      );
    }
  } else {
    rules.push(
      intl
        ? intl.formatMessage({ id: 'cancellation_not_allowed_after_start' })
        : '• No cancellations allowed after booking starts',
    );
  }

  const rulesText = rules.filter(Boolean).join('<br/>');
  if (rulesText && customMessage) {
    return `${rulesText}<br/><br/>${customMessage}`;
  }

  return rulesText || customMessage;
};

export const getCancellationConsequenceText = (
  data: CancellationPolicyData & { startAt?: string | Date; now?: Date },
  intl: IntlShape,
  isPaidResource = true,
): string | null => {
  const {
    allowCancelDuring = false,
    allowLateCancel = false,
    cancelDuringFeeAmount,
    cancelDuringFeePercent,
    lateCancelFeeAmount,
    lateCancelFeePercent,
    cancellationWindowEndMinutes,
    startAt,
    currencyType,
    locale,
  } = data;
  if (!startAt) return null;

  const startAtDate = new Date(startAt);
  if (Number.isNaN(startAtDate.getTime())) return null;

  const currentTime = data.now ?? new Date();
  const minutesBeforeStart = (startAtDate.getTime() - currentTime.getTime()) / (MINUTES_IN_HOUR * 1000);
  const isAfterStart = currentTime.getTime() >= startAtDate.getTime();
  const isWithinCancellationWindow =
    cancellationWindowEndMinutes !== null &&
    cancellationWindowEndMinutes !== undefined &&
    cancellationWindowEndMinutes >= 0 &&
    minutesBeforeStart <= cancellationWindowEndMinutes;

  const isLateCancellation =
    (isAfterStart && allowCancelDuring) || (!isAfterStart && allowLateCancel && isWithinCancellationWindow);

  if (!isLateCancellation) {
    return null;
  }

  if (isPaidResource) {
    const cancelDuringFeeText = formatFee(cancelDuringFeeAmount, cancelDuringFeePercent, currencyType, locale);
    const lateFeeText = formatFee(lateCancelFeeAmount, lateCancelFeePercent, currencyType, locale);
    const feeText = isAfterStart ? cancelDuringFeeText : lateFeeText;

    if (feeText) {
      return intl.formatMessage({ id: 'cancellation_charge_warning' }, { fee: feeText });
    }
  }

  return intl.formatMessage({ id: 'cancellation_late_warning' });
};
